// src/pages/AssetMasterForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Form, Row, Col, Button, Spinner } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { toast } from "react-toastify";
import axios from "axios";
const initialForm = {
  organisationId: "",
  userId: "",
  assetItemId: "",
  assetName: "",
  assetDescription: "",
  assetCategoryId: "",
  parentAssetId: "",
  capitalizationDate: "",
  usefulLifeYears: "",
  depreciationMethodId: "",
  depreciationRate: "",
  depreciationStartBasisId: "",
  assetCapacity: "",
  assetCapacityUnit: "",
  specification: "",
  warrantyStartDate: "",
  warrantyEndDate: "",
};

export default function AssetMasterForm() {
  const { user } = useAuth();

  const [form, setForm] = useState(initialForm);

  // Dropdown option states
  const [assetItems, setAssetItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [deprMethods, setDeprMethods] = useState([]);
  const [deprStartBases, setDeprStartBases] = useState([]);
  const [units, setUnits] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState({
    assetItems: false,
    categories: false,
    assets: false,
    deprMethods: false,
    deprStartBases: false,
    units: false,
  });
  const [errors, setErrors] = useState({});

  // Pre-fill read-only fields from auth
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      organisationId: user?.organisationId ?? "",
      userId: user?.userId ?? "",
    }));
  }, [user]);

  // Generic fetch helper
  const fetcher = async (url, setStateKey) => {
    setLoading((l) => ({ ...l, [setStateKey]: true }));
    try {
      const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data;
    } catch (e) {
      console.error(`Failed to fetch ${url}:`, e);
      return [];
    } finally {
      setLoading((l) => ({ ...l, [setStateKey]: false }));
    }
  };

  // Load all dropdowns on mount
  useEffect(() => {
    (async () => {
      const [
        assetItemData,
        categoryData,
        assetsData,
        deprMethodData,
        deprStartData,
        unitsData,
      ] = await Promise.all([
        fetcher(`${Base_URL}/Asset/AssetItems`, "assetItems"),
        fetcher(`${Base_URL}/Asset/getCategory`, "categories"),
        fetcher(`${Base_URL}/Asset`, "assets"),
        fetcher(`${Base_URL}/Asset/getDepriciation`, "deprMethods"),
        fetcher(`${Base_URL}/Asset/getDepricaionStart`, "deprStartBases"),
        fetcher(`${Base_URL}/UnitMaster/GetAllUnits`, "units"),
      ]);

      // Normalize arrays (defensive)
      setAssetItems(Array.isArray(assetItemData) ? assetItemData : []);
      setCategories(Array.isArray(categoryData) ? categoryData : []);
      setAssets(Array.isArray(assetsData) ? assetsData : []);
      setDeprMethods(Array.isArray(deprMethodData) ? deprMethodData : []);
      setDeprStartBases(Array.isArray(deprStartData) ? deprStartData : []);
      setUnits(Array.isArray(unitsData) ? unitsData : []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived UI flags
  const anyLoading = useMemo(
    () =>
      loading.assetItems ||
      loading.categories ||
      loading.assets ||
      loading.deprMethods ||
      loading.deprStartBases ||
      loading.units,
    [loading]
  );

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Simple validation
  const validate = () => {
    const next = {};
    if (!form.organisationId) next.organisationId = "Required";
    if (!form.userId) next.userId = "Required";
    if (!form.assetItemId) next.assetItemId = "Select an Asset Item";
    if (!form.assetName?.trim()) next.assetName = "Enter Asset Name";
    if (!form.assetCategoryId) next.assetCategoryId = "Select a Category";
    if (!form.capitalizationDate) next.capitalizationDate = "Select a date";
    if (
      form.usefulLifeYears === "" ||
      isNaN(Number(form.usefulLifeYears)) ||
      Number(form.usefulLifeYears) < 0
    )
      next.usefulLifeYears = "Enter valid years";
    if (!form.depreciationMethodId)
      next.depreciationMethodId = "Select a method";
    if (
      form.depreciationRate === "" ||
      isNaN(Number(form.depreciationRate)) ||
      Number(form.depreciationRate) < 0
    )
      next.depreciationRate = "Enter valid rate";
    if (!form.depreciationStartBasisId)
      next.depreciationStartBasisId = "Select start basis";
    if (form.assetCapacity !== "" && isNaN(Number(form.assetCapacity)))
      next.assetCapacity = "Enter numeric capacity";
    if (form.warrantyStartDate && form.warrantyEndDate) {
      if (new Date(form.warrantyEndDate) < new Date(form.warrantyStartDate)) {
        next.warrantyEndDate = "End date must be after start date";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async(e) => {
    e.preventDefault();
    if (!validate()) return;

    // Build payload (numbers as numbers where appropriate)
    const payload = {
      organisationId: form.organisationId,
      userId: form.userId,
      assetItemId: form.assetItemId || null,
      assetName: form.assetName?.trim(),
      assetDescription: form.assetDescription?.trim() || null,
      assetCategoryId: form.assetCategoryId
       ||null,
      parentAssetId: form.parentAssetId || null,
      capitalizationDate: form.capitalizationDate || null,
      usefulLifeYears:
        form.usefulLifeYears !== "" ? Number(form.usefulLifeYears) : null,
      depreciationMethodId: form.depreciationMethodId
       || null,
      depreciationRate:
        form.depreciationRate||null,
      depreciationStartBasisId: form.depreciationStartBasisId
       || null,
      assetCapacity:
        form.assetCapacity !== "" ? Number(form.assetCapacity) : null,
      assetCapacityUnit: form.assetCapacityUnit || null, // unit "code"
      specification: form.specification?.trim() || null,
      warrantyStartDate: form.warrantyStartDate || null,
      warrantyEndDate: form.warrantyEndDate || null,
    };

    try{
        const res=await axios.post(`${Base_URL}/asset/createAsset`,payload);
        if(res.status===200)
        {
            toast.success(res.data," Asset Created Successfully");
        }
        else
        {
            toast.error("Error Creating Asset")
        }
    }
    catch(error)
    {
        toast.error(error);
    }
    // TODO: Replace console.log with POST call when backend is ready
    // fetch(`${Base_URL}/Asset/create`, { method: "POST", headers: {...}, body: JSON.stringify(payload) })
  };

  // Helpers to render option labels safely
  const labelOr = (text, fallback = "-") =>
    (text ?? "").toString().trim() || fallback;

  return (
    <div className="container py-3">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h4 className="m-0">Asset Master</h4>
        {anyLoading && (
          <span className="d-inline-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> Loading lookups…
          </span>
        )}
      </div>

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          {/* Organisation Id (read-only) */}
          <Col md={6}>
            <Form.Group controlId="organisationId">
              <Form.Label>Organisation Id</Form.Label>
              <Form.Control
                type="text"
                name="organisationId"
                value={form.organisationId}
                readOnly
                plaintext={false}
                onChange={() => {}}
                isInvalid={!!errors.organisationId}
              />
              <Form.Control.Feedback type="invalid">
                {errors.organisationId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* User Id (read-only) */}
          <Col md={6}>
            <Form.Group controlId="userId">
              <Form.Label>User Id</Form.Label>
              <Form.Control
                type="text"
                name="userId"
                value={form.userId}
                readOnly
                plaintext={false}
                onChange={() => {}}
                isInvalid={!!errors.userId}
              />
              <Form.Control.Feedback type="invalid">
                {errors.userId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Asset Item */}
          <Col md={6}>
            <Form.Group controlId="assetItemId">
              <Form.Label>Asset Item</Form.Label>
              <Form.Select
                name="assetItemId"
                value={form.assetItemId}
                onChange={handleChange}
                disabled={loading.assetItems}
                isInvalid={!!errors.assetItemId}
              >
                <option value="">-- Select Asset Item --</option>
                {assetItems.map((it) => (
                  <option key={it.itemCodeId} value={it.itemCodeId}>
                    {`${labelOr(it.itemCodeId)} - ${labelOr(it.itemName)}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.assetItemId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Asset Name */}
          <Col md={6}>
            <Form.Group controlId="assetName">
              <Form.Label>Asset Name</Form.Label>
              <Form.Control
                type="text"
                name="assetName"
                value={form.assetName}
                onChange={handleChange}
                isInvalid={!!errors.assetName}
                placeholder="Enter asset name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.assetName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Asset Description */}
          <Col md={12}>
            <Form.Group controlId="assetDescription">
              <Form.Label>Asset Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="assetDescription"
                value={form.assetDescription}
                onChange={handleChange}
                placeholder="Optional description"
              />
            </Form.Group>
          </Col>

          {/* Asset Category */}
          <Col md={6}>
            <Form.Group controlId="assetCategoryId">
              <Form.Label>Asset Category</Form.Label>
              <Form.Select
                name="assetCategoryId"
                value={form.assetCategoryId}
                onChange={handleChange}
                disabled={loading.categories}
                isInvalid={!!errors.assetCategoryId}
              >
                <option value="">-- Select Category --</option>
                {categories.map((c) => (
                  <option key={c.assetCategoryId} value={c.assetCategoryId}>
                    {`${labelOr(c.assetCategoryId)} - ${labelOr(
                      c.assetCategoryDescription
                    )}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.assetCategoryId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Parent Asset */}
          <Col md={6}>
            <Form.Group controlId="parentAssetId">
              <Form.Label>Parent Asset</Form.Label>
              <Form.Select
                name="parentAssetId"
                value={form.parentAssetId}
                onChange={handleChange}
                disabled={loading.assets}
              >
                <option value="">-- Select Parent Asset (optional) --</option>
                {assets.map((a) => (
                  <option key={a.assetId ?? a.id} value={a.assetId ?? a.id}>
                    {`${labelOr(a.assetId ?? a.id)} - ${labelOr(
                      a.assetName ?? a.name
                    )}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Capitalization Date */}
          <Col md={6}>
            <Form.Group controlId="capitalizationDate">
              <Form.Label>Capitalization Date</Form.Label>
              <Form.Control
                type="date"
                name="capitalizationDate"
                value={form.capitalizationDate}
                onChange={handleChange}
                isInvalid={!!errors.capitalizationDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.capitalizationDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Useful Life Years */}
          <Col md={6}>
            <Form.Group controlId="usefulLifeYears">
              <Form.Label>Useful Life (Years)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                name="usefulLifeYears"
                value={form.usefulLifeYears}
                onChange={handleChange}
                isInvalid={!!errors.usefulLifeYears}
                placeholder="e.g., 5"
              />
              <Form.Control.Feedback type="invalid">
                {errors.usefulLifeYears}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Depreciation Method */}
          <Col md={6}>
            <Form.Group controlId="depreciationMethodId">
              <Form.Label>Depreciation Method</Form.Label>
              <Form.Select
                name="depreciationMethodId"
                value={form.depreciationMethodId}
                onChange={handleChange}
                disabled={loading.deprMethods}
                isInvalid={!!errors.depreciationMethodId}
              >
                <option value="">-- Select Method --</option>
                {deprMethods.map((m) => (
                  <option
                    key={m.depreciationMethodId}
                    value={m.depreciationMethodId}
                  >
                    {`${labelOr(m.depreciationMethodId)} - ${labelOr(
                      m.depreciationMethodName
                    )}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.depreciationMethodId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Depreciation Rate */}
          <Col md={6}>
            <Form.Group controlId="depreciationRate">
              <Form.Label>Depreciation Rate (%)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                name="depreciationRate"
                value={form.depreciationRate}
                onChange={handleChange}
                isInvalid={!!errors.depreciationRate}
                placeholder="e.g., 15"
              />
              <Form.Control.Feedback type="invalid">
                {errors.depreciationRate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Depreciation Start Basis */}
          <Col md={6}>
            <Form.Group controlId="depreciationStartBasisId">
              <Form.Label>Depreciation Start Basis</Form.Label>
              <Form.Select
                name="depreciationStartBasisId"
                value={form.depreciationStartBasisId}
                onChange={handleChange}
                disabled={loading.deprStartBases}
                isInvalid={!!errors.depreciationStartBasisId}
              >
                <option value="">-- Select Start Basis --</option>
                {deprStartBases.map((b) => (
                  <option
                    key={b.depreciationStartBasisId}
                    value={b.depreciationStartBasisId}
                  >
                    {`${labelOr(b.depreciationStartBasisId)} - ${labelOr(
                      b.depreciationStartBasisName
                    )}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.depreciationStartBasisId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Asset Capacity */}
          <Col md={6}>
            <Form.Group controlId="assetCapacity">
              <Form.Label>Asset Capacity</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="assetCapacity"
                value={form.assetCapacity}
                onChange={handleChange}
                isInvalid={!!errors.assetCapacity}
                placeholder="e.g., 100"
              />
              <Form.Control.Feedback type="invalid">
                {errors.assetCapacity}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Asset Capacity Unit */}
          <Col md={6}>
            <Form.Group controlId="assetCapacityUnit">
              <Form.Label>Asset Capacity Unit</Form.Label>
              <Form.Select
                name="assetCapacityUnit"
                value={form.assetCapacityUnit}
                onChange={handleChange}
                disabled={loading.units}
              >
                <option value="">-- Select Unit --</option>
                {units.map((u) => (
                  <option key={u.code} value={u.code}>
                    {`${labelOr(u.code)} - ${labelOr(u.description)}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Specification */}
          <Col md={12}>
            <Form.Group controlId="specification">
              <Form.Label>Specification</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="specification"
                value={form.specification}
                onChange={handleChange}
                placeholder="Technical specifications, model details, etc."
              />
            </Form.Group>
          </Col>

          {/* Warranty Dates */}
          <Col md={6}>
            <Form.Group controlId="warrantyStartDate">
              <Form.Label>Warranty Start Date</Form.Label>
              <Form.Control
                type="date"
                name="warrantyStartDate"
                value={form.warrantyStartDate}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="warrantyEndDate">
              <Form.Label>Warranty End Date</Form.Label>
              <Form.Control
                type="date"
                name="warrantyEndDate"
                value={form.warrantyEndDate}
                onChange={handleChange}
                isInvalid={!!errors.warrantyEndDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.warrantyEndDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} className="d-flex justify-content-end gap-2 mt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setForm({
                  ...initialForm,
                  organisationId: user?.organisationId ?? "",
                  userId: user?.userId ?? "",
                });
                setErrors({});
              }}
            >
              Reset
            </Button>
            <Button type="submit" variant="primary" disabled={anyLoading}>
              {anyLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Please wait…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
